import React, { useState } from 'react';
import {
  Button,
  Divider,
  Form,
  InputNumber,
  Popover,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CargoTableRow } from './sharedTransportFormRows';
import { deriveRemainingElsewhereQuantity } from './sharedTransportFormRows';
import { buildTransportGoodsValueAnchorId } from './transportRequiredFields';
import {
  DEFAULT_TRANSPORT_CURRENCY,
  SUPPLIER_HINT_CURRENCY,
  calculateCargoLineMetrics,
  cargoLineMetricsAreEmpty,
  computeSupplierPurchaseDisplayAmounts,
  type CargoHintProvenanceState,
} from './sharedTransportFormUtils';
import {
  MAX_MONEY_AMOUNT,
  PACKAGING_BOUNDS,
  POLISH_DECIMAL_INPUT_NUMBER_PROPS,
  manualLdmRule,
  packagingPositiveDecimalRule,
  packagingStackLayersRule,
  type AllocationMethod,
  type TransportCostAllocationItemResult,
} from '../utils/transportCostAllocation';
import { formatUnit } from '../utils/units';
import { formatCurrencyUnitPrice, formatMoneyPl } from '../utils/currency';
import { formatDate } from '../utils/dateUtils';
import { isCargoOverAllocated } from '../utils/transportCargoAllocation';
import CommercialQuantityEditor from '../modules/orders/components/CommercialQuantityEditor';
import {
  buildQuantitySetFromPair,
  COMMERCIAL_UNITS,
  getQuantitySetValue,
  isAllocationUnit,
  MAX_CUSTOMER_PHYSICAL_QUANTITY,
  MAX_CUSTOMER_PRICING_QUANTITY,
  quantitySetToFields,
  resolveStoredQuantitySet,
} from '../utils/commercialQuantity';
import type { AllocationUnit } from '../utils/dimensionConversion';
import './TransportCargoItemCard.css';

const { Text } = Typography;

interface GoodsValueControlProps {
  value?: number | null;
  onChange?: (value: number | null) => void;
  currency: string;
  onOperatorEdit: () => void;
}

const GoodsValueControl: React.FC<GoodsValueControlProps> = ({
  value,
  onChange,
  currency,
  onOperatorEdit,
}) => {
  const { t } = useTranslation('transport');
  const [editing, setEditing] = useState(false);
  const numericValue = value === null || value === undefined ? null : Number(value);
  const displayValue = numericValue !== null && Number.isFinite(numericValue)
    ? formatMoneyPl(numericValue, currency)
    : `— ${currency}`;

  if (!editing) {
    return (
      <div className="transport-cargo-goods-value-display">
        <Text className="transport-cargo-goods-value-amount">{displayValue}</Text>
        <Tooltip title={t('goodsValueEdit')}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            aria-label={t('goodsValueEdit')}
            onClick={() => setEditing(true)}
          />
        </Tooltip>
      </div>
    );
  }

  return (
    <Space.Compact block>
      <InputNumber
        autoFocus
        precision={2}
        addonAfter={currency}
        value={value}
        onChange={(nextValue) => {
          onOperatorEdit();
          onChange?.(nextValue);
        }}
        onPressEnter={() => setEditing(false)}
        aria-label={t('commercialAllocationGoodsValue')}
        style={{ width: '100%' }}
        {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
      />
      <Tooltip title={t('goodsValueFinishEditing')}>
        <Button
          icon={<CheckOutlined />}
          aria-label={t('goodsValueFinishEditing')}
          onClick={() => setEditing(false)}
        />
      </Tooltip>
    </Space.Compact>
  );
};

interface TransportCargoItemCardProps {
  record: CargoTableRow;
  canEditTransportCosts: boolean;
  currency?: string;
  costAllocationMethod: AllocationMethod;
  showUnitCosts: boolean;
  allocationItem?: TransportCostAllocationItemResult;
  formatCurrencyAmount: (value: number | null | undefined) => string;
  hintProvenance?: CargoHintProvenanceState;
  onGoodsValueTouched: (key: string) => void;
  onQuantityTouched?: (key: string) => void;
  onRemoveCargo: (key: string) => void;
}

const TransportCargoItemCard: React.FC<TransportCargoItemCardProps> = ({
  record,
  canEditTransportCosts,
  currency,
  costAllocationMethod,
  showUnitCosts,
  allocationItem,
  formatCurrencyAmount,
  hintProvenance,
  onGoodsValueTouched,
  onQuantityTouched = () => undefined,
  onRemoveCargo,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const displayCurrency = currency || DEFAULT_TRANSPORT_CURRENCY;
  const form = Form.useFormInstance();
  const overflow = isCargoOverAllocated(record.quantity, record.allocatedElsewhere, record.availableQuantity);
  const remaining = deriveRemainingElsewhereQuantity(
    record.availableQuantity,
    record.allocatedElsewhere,
    record.quantity,
  );
  const availableBeforeCurrent = Math.max(
    record.availableQuantity - record.allocatedElsewhere,
    0,
  );
  const physicalUnit = record.unit as AllocationUnit | undefined;
  const commercialUnit = record.commercialUnit as AllocationUnit | null | undefined;
  const recordQuantitySet = resolveStoredQuantitySet(record as unknown as Record<string, unknown>);
  const alternateEntryUnits = COMMERCIAL_UNITS.filter((unit) => (
    unit !== physicalUnit && getQuantitySetValue(recordQuantitySet, unit) !== null
  ));
  const conversionUnit = (
    commercialUnit
    && commercialUnit !== physicalUnit
    && alternateEntryUnits.includes(commercialUnit)
  ) ? commercialUnit : alternateEntryUnits[0];
  const conversionQuantity = conversionUnit
    ? getQuantitySetValue(recordQuantitySet, conversionUnit)
    : null;
  const supportsCommercialEntry = isAllocationUnit(physicalUnit)
    && Boolean(conversionUnit)
    && conversionQuantity !== null;
  const metrics = calculateCargoLineMetrics(record, Number(record.quantity || 0));
  const concreteHint = hintProvenance?.kind === 'value'
    || hintProvenance?.kind === 'currencyMismatch'
    ? hintProvenance.hint
    : null;
  const purchaseDisplay = concreteHint
    ? computeSupplierPurchaseDisplayAmounts(concreteHint, record.quantity, record)
    : null;
  const hasOriginalPurchaseValue = purchaseDisplay?.sourceAmount !== null
    && purchaseDisplay?.sourceAmount !== undefined
    && Boolean(purchaseDisplay.sourceCurrency);
  const hasPackaging = Boolean(record.piecesPerPackage && record.packageLength && record.packageWidth);
  const packagingSummary = hasPackaging
    ? t('transport:packagingSummary', {
      packages: record.piecesPerPackage,
      length: record.packageLength,
      width: record.packageWidth,
      layers: record.packageStackLayers ?? 1,
    })
    : t('transport:packagingSummaryUnknown');

  const formatMetric = (value: number, fractionDigits: number): string =>
    value.toLocaleString('pl-PL', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  const formatHintQuantity = (value: number): string =>
    value.toLocaleString('pl-PL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });

  const formatHintDate = (value: string | null | undefined): string | null => {
    const isoDate = formatDate(value);
    if (isoDate === '-') {
      return null;
    }
    const [year, month, day] = isoDate.split('-');
    return `${day}.${month}.${year}`;
  };

  const renderExchangeRateDetails = (): React.ReactNode => {
    if (!concreteHint || !purchaseDisplay?.sourceCurrency
      || purchaseDisplay.sourceCurrency === SUPPLIER_HINT_CURRENCY) {
      return null;
    }

    const rate = Number(concreteHint.exchangeRateToPLN);
    if (!Number.isFinite(rate) || rate <= 0) {
      return null;
    }

    const source = String(concreteHint.exchangeRateToPLNSource || '').toUpperCase();
    const effectiveDate = formatHintDate(concreteHint.exchangeRateToPLNEffectiveDate);
    const requestedDate = formatHintDate(concreteHint.exchangeRateToPLNRequestedDate);
    const rateValues = {
      currency: purchaseDisplay.sourceCurrency,
      rate: formatMetric(rate, 4),
      ...(effectiveDate ? { date: effectiveDate } : {}),
    };
    const datedKey = source === 'FALLBACK'
      ? 'transport:goodsValueHint.exchangeRateFallback'
      : source.startsWith('NBP')
        ? 'transport:goodsValueHint.exchangeRateNbp'
        : 'transport:goodsValueHint.exchangeRateSaved';
    const undatedKey = source === 'FALLBACK'
      ? 'transport:goodsValueHint.exchangeRateFallbackUndated'
      : source.startsWith('NBP')
        ? 'transport:goodsValueHint.exchangeRateNbpUndated'
        : 'transport:goodsValueHint.exchangeRateSavedUndated';

    return (
      <Text
        type={source === 'FALLBACK' ? 'warning' : 'secondary'}
        className="transport-cargo-purchase-rate"
      >
        {source === 'FALLBACK' ? <><WarningOutlined />{' '}</> : null}
        {t(effectiveDate ? datedKey : undatedKey, rateValues)}
        {concreteHint.exchangeRateToPLNTableNumber
          ? ` · ${t('transport:goodsValueHint.exchangeRateTable', {
            number: concreteHint.exchangeRateToPLNTableNumber,
          })}`
          : null}
        {requestedDate && requestedDate !== effectiveDate
          ? ` · ${t('transport:goodsValueHint.exchangeRateRequestedDate', {
            date: requestedDate,
          })}`
          : null}
      </Text>
    );
  };

  const renderSourcePurchaseValue = (): React.ReactNode => {
    if (!concreteHint || !purchaseDisplay || !hasOriginalPurchaseValue) {
      return null;
    }

    const sourceCurrency = purchaseDisplay.sourceCurrency || SUPPLIER_HINT_CURRENCY;
    const basis = hintProvenance?.kind === 'value'
      || hintProvenance?.kind === 'currencyMismatch'
      ? hintProvenance.basis
      : null;
    let contextKey = 'transport:goodsValueHint.sourceContextLine';
    const contextValues: Record<string, unknown> = {
      number: concreteHint.supplierOrderDisplayNumber || '—',
      quantity: formatHintQuantity(purchaseDisplay.purchaseBasisQuantity),
      unit: formatUnit(
        basis?.kind === 'wholeOrder'
          ? (basis.purchaseUnit || record.unit)
          : (concreteHint.supplierOrderItemUnit || concreteHint.allocatedUnit || record.unit),
      ),
    };

    if (basis?.kind === 'wholeOrder') {
      const isFullLoad = Math.abs(
        purchaseDisplay.purchaseBasisQuantity - basis.purchaseQuantity,
      ) < 0.0005;
      contextKey = isFullLoad
        ? 'transport:goodsValueHint.sourceContextWhole'
        : 'transport:goodsValueHint.sourceContextPartial';
      contextValues.total = formatHintQuantity(basis.purchaseQuantity);
    }

    return (
      <div
        className="transport-cargo-purchase-source"
        data-testid="transport-cargo-purchase-source"
      >
        <Text type="secondary" className="transport-cargo-label">
          {t('transport:goodsValueHint.sourcePurchaseLabel')}
        </Text>
        <div className="transport-cargo-purchase-amounts">
          <Text strong>
            {formatMoneyPl(purchaseDisplay.sourceAmount as number, sourceCurrency)}
          </Text>
          {sourceCurrency !== SUPPLIER_HINT_CURRENCY ? (
            <Text type="secondary">
              ({formatMoneyPl(purchaseDisplay.plnAmount, SUPPLIER_HINT_CURRENCY)})
            </Text>
          ) : null}
        </div>
        <Text type="secondary" className="transport-cargo-purchase-context">
          {t(contextKey, contextValues)}
        </Text>
        {renderExchangeRateDetails()}
      </div>
    );
  };

  const renderGoodsValueProvenance = (): React.ReactNode => {
    if (!hintProvenance || hintProvenance.kind === 'none') {
      return null;
    }

    if (hintProvenance.kind === 'ambiguous') {
      return (
        <Text type="secondary" className="transport-cargo-provenance">
          <WarningOutlined /> {t('transport:goodsValueHint.ambiguous')}
        </Text>
      );
    }

    if (hintProvenance.kind === 'currencyMismatch') {
      return (
        <Text type="warning" className="transport-cargo-provenance">
          <WarningOutlined /> {t('transport:goodsValueHint.currencyMismatch')}
        </Text>
      );
    }

    const { hint, basis } = hintProvenance;
    if (hasOriginalPurchaseValue) {
      return null;
    }
    if (basis.kind === 'wholeOrder') {
      return (
        <Text type="secondary" className="transport-cargo-provenance">
          <InfoCircleOutlined /> {t('transport:goodsValueHint.prefilledFromTotal', {
            number: hint.supplierOrderDisplayNumber || '—',
            value: formatMoneyPl(basis.purchaseValuePLN, SUPPLIER_HINT_CURRENCY),
            quantity: basis.purchaseQuantity.toLocaleString('pl-PL'),
            unit: formatUnit(basis.purchaseUnit || record.unit),
          })}
        </Text>
      );
    }
    return (
      <Text type="secondary" className="transport-cargo-provenance">
        <InfoCircleOutlined /> {t('transport:goodsValueHint.prefilled', {
          number: hint.supplierOrderDisplayNumber || '—',
          unitCost: formatCurrencyUnitPrice(basis.unitPurchaseCostPLN, SUPPLIER_HINT_CURRENCY),
          rate: formatMetric(Number(hint.exchangeRateToPLN || 0), 4),
          currency: hint.sourceCurrency || '—',
        })}
      </Text>
    );
  };

  const packagingEditor = canEditTransportCosts ? (
    <Popover
      trigger="click"
      placement="bottom"
      content={(
        <Space direction="vertical" size={8} style={{ width: 240 }}>
          <Form.Item
            name={['cargoItemsByKey', record.key, 'piecesPerPackage']}
            label={t('transport:packagingPiecesPerPackage')}
            style={{ marginBottom: 0 }}
            rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPiecesPerPackage)]}
          >
            <InputNumber step={1} style={{ width: '100%' }} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
          </Form.Item>
          <Form.Item
            name={['cargoItemsByKey', record.key, 'packageLength']}
            label={t('transport:packagingPackageLength')}
            style={{ marginBottom: 0 }}
            rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPackageDimensionMm)]}
          >
            <InputNumber
              step={1}
              placeholder={String(record.length ?? '')}
              style={{ width: '100%' }}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
            />
          </Form.Item>
          <Form.Item
            name={['cargoItemsByKey', record.key, 'packageWidth']}
            label={t('transport:packagingPackageWidth')}
            style={{ marginBottom: 0 }}
            rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPackageDimensionMm)]}
          >
            <InputNumber
              step={1}
              placeholder={String(record.width ?? '')}
              style={{ width: '100%' }}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
            />
          </Form.Item>
          <Form.Item
            name={['cargoItemsByKey', record.key, 'packageStackLayers']}
            label={t('transport:packagingStackLayers')}
            style={{ marginBottom: 0 }}
            rules={[packagingStackLayersRule(t, PACKAGING_BOUNDS.minStackLayers, PACKAGING_BOUNDS.maxStackLayers)]}
          >
            <InputNumber step={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Divider style={{ margin: '4px 0' }} />
          <Form.Item
            name={['cargoItemsByKey', record.key, 'manualLdm']}
            label={t('transport:manualLdmLabel')}
            tooltip={t('transport:manualLdmTooltip')}
            style={{ marginBottom: 0 }}
            rules={[manualLdmRule(t, PACKAGING_BOUNDS.maxManualLdm)]}
          >
            <InputNumber step={0.1} style={{ width: '100%' }} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
          </Form.Item>
        </Space>
      )}
      title={t('transport:packagingEditorTitle')}
    >
      <Button
        type={hasPackaging ? 'default' : 'dashed'}
        icon={<EditOutlined />}
        className={hasPackaging ? undefined : 'transport-cargo-packaging-missing'}
        aria-label={hasPackaging
          ? t('transport:packagingEditTooltip')
          : t('transport:packagingFillAction')}
      >
        {hasPackaging
          ? t('transport:packagingEditAction')
          : t('transport:packagingFillAction')}
      </Button>
    </Popover>
  ) : null;

  const unitCostItems = [
    [t('transport:commercialAllocationGoodsPerUnit'), allocationItem?.goodsCostPerUnit],
    [t('transport:commercialAllocationTransportPerUnit'), allocationItem?.transportCostPerUnit],
    [t('transport:commercialAllocationTransportPerSqm'), allocationItem?.transportCostPerAreaSqm],
    [t('transport:commercialAllocationTransportPerCbm'), allocationItem?.transportCostPerVolumeM3],
    [t('transport:commercialAllocationTransportPerLdm'), allocationItem?.transportCostPerLdm],
    [t('transport:commercialAllocationTotalPerUnit'), allocationItem?.landedCostPerUnit],
    [t('transport:commercialAllocationTotal'), allocationItem?.landedCostTotal],
  ] as const;

  return (
    <article className="transport-cargo-card" data-testid={`transport-cargo-card-${record.key}`}>
      <header className="transport-cargo-card-header" data-testid="transport-cargo-card-header">
        <div className="transport-cargo-product">
          <Text strong className="transport-cargo-product-name">{record.productName}</Text>
          {record.productDimensions ? (
            <Text type="secondary" className="transport-cargo-product-dimensions">
              {record.productDimensions}
            </Text>
          ) : null}
          {(record.productVariantLabels || []).length > 0 ? (
            <Space size={[4, 4]} wrap className="transport-cargo-product-labels">
              {(record.productVariantLabels || []).map((label, index) => (
                <Tag
                  key={`${label.key || 'label'}-${label.value}-${index}`}
                  bordered={false}
                  color={label.isBaseProduct ? 'default' : 'blue'}
                >
                  {label.key ? `${label.key}: ${label.value}` : label.value}
                </Tag>
              ))}
            </Space>
          ) : null}
          <Text type="secondary" className="transport-cargo-order-reference">
            {t('transport:orderNumber')}: <Text>{record.orderNumber}</Text>
          </Text>
        </div>
        <Button
          danger
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          disabled={Number(record.quantity || 0) <= 0}
          aria-label={t('transport:cargo.removeFromLoad')}
          data-testid={`transport-cargo-remove-${record.key}`}
          onClick={() => onRemoveCargo(record.key)}
        >
          {t('transport:cargo.removeFromLoad')}
        </Button>
      </header>

      <div className="transport-cargo-card-main" data-testid="transport-cargo-card-grid">
        <section className="transport-cargo-block transport-cargo-availability">
          <Text type="secondary" className="transport-cargo-label">{t('transport:cargo.availability')}</Text>
          <Text>{t('transport:cargo.inOrderValue', {
            quantity: record.availableQuantity,
            unit: formatUnit(record.unit),
          })}</Text>
          <Text>
            {t('transport:cargo.elsewhereValue', {
              quantity: record.allocatedElsewhere,
              unit: formatUnit(record.unit),
            })}
          </Text>
          <Text strong>
            {t('transport:cargo.availableForTransportValue', {
              quantity: availableBeforeCurrent,
              unit: formatUnit(record.unit),
            })}
          </Text>
          <Text type="secondary">
            {t('transport:cargo.remainingAfterTransport', {
              quantity: remaining,
              unit: formatUnit(record.unit),
            })}
          </Text>
        </section>

        <section className="transport-cargo-block transport-cargo-quantity">
          <Text type="secondary" className="transport-cargo-label">{t('transport:cargo.loadedQuantity')}</Text>
          <Form.Item
            name={['cargoItemsByKey', record.key, 'quantity']}
            hidden
            rules={[{
              validator: (_, value) => {
                const quantity = Number(value);
                return Number.isFinite(quantity) && quantity >= 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('transport:commercialAllocationQuantityPositive')));
              },
            }]}
          >
            <InputNumber />
          </Form.Item>
          <div className="transport-cargo-quantity-editor">
            <InputNumber
              value={record.quantity}
              precision={3}
              addonAfter={formatUnit(record.unit)}
              status={overflow ? 'warning' : undefined}
              aria-label={t('transport:cargo.loadedQuantity')}
              onFocus={() => onQuantityTouched(record.key)}
              onChange={(next) => form.setFieldValue(['cargoItemsByKey', record.key, 'quantity'], next)}
              style={{ width: '100%' }}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
            />
            {supportsCommercialEntry ? (
              <CommercialQuantityEditor
              value={{
                physicalQuantity: Number(record.quantity || 0),
                physicalUnit: physicalUnit as AllocationUnit,
                pricingQuantity: Number(record.quantity || 0),
                pricingUnit: physicalUnit,
                quantityPcs: record.quantityPcs ?? null,
                quantityM2: record.quantityM2 ?? null,
                quantityM3: record.quantityM3 ?? null,
                conversionQuantity: Number(conversionQuantity),
                conversionUnit,
              }}
              onChange={(next) => {
                onQuantityTouched(record.key);
                const nextQuantitySet = buildQuantitySetFromPair(next, record);
                const current = form.getFieldValue(['cargoItemsByKey', record.key]) || {};
                form.setFieldValue(
                  ['cargoItemsByKey', record.key],
                  {
                    ...current,
                    quantity: next.physicalQuantity,
                    unit: next.physicalUnit,
                    ...(nextQuantitySet ? quantitySetToFields(nextQuantitySet) : {}),
                  },
                );
              }}
              maxPhysicalQuantity={Math.min(
                Math.max(availableBeforeCurrent, Number(record.quantity || 0)),
                MAX_CUSTOMER_PHYSICAL_QUANTITY,
              )}
              maxPricingQuantity={MAX_CUSTOMER_PRICING_QUANTITY}
              physicalUnit={physicalUnit as AllocationUnit}
              sourceUnitOptions={COMMERCIAL_UNITS}
              pricingConversionUnits={COMMERCIAL_UNITS}
              triggerOnly
              triggerLabel={t('transport:cargo.convertToUnit')}
              allowOutOfBounds
              outOfBoundsMessage={t('transport:cargo.overAllocatedBlocked')}
              width={record.width}
              length={record.length}
              height={record.height}
              thickness={record.thickness}
              />
            ) : null}
          </div>
          {overflow ? <Text type="warning">{t('transport:cargo.overAllocatedBlocked')}</Text> : null}
        </section>

        <section className="transport-cargo-block transport-cargo-metrics">
          <Text type="secondary" className="transport-cargo-label">{t('transport:cargo.metrics')}</Text>
          {Number(record.quantity || 0) <= 0 ? (
            <Text type="secondary">{t('transport:cargo.metricsZeroQuantity')}</Text>
          ) : cargoLineMetricsAreEmpty(metrics) ? (
            <Text type="secondary">{t('transport:cargo.metricsMissingDimensions')}</Text>
          ) : (
            <Space direction="vertical" size={0}>
              <Text>{`${formatMetric(metrics.areaSqm ?? 0, 2)} m²`}</Text>
              <Text>{`${formatMetric(metrics.volume ?? 0, 3)} m³`}</Text>
              <Space size={6} wrap>
                <Text>{metrics.ldm === null ? '— LDM' : `${formatMetric(metrics.ldm, 2)} LDM`}</Text>
                {metrics.ldm === null ? <WarningOutlined className="transport-cargo-warning-icon" /> : null}
                {metrics.ldmIsManual ? (
                  <Tooltip title={t('transport:manualLdmActiveTooltip')}>
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>{t('transport:manualLdmTag')}</Tag>
                  </Tooltip>
                ) : null}
              </Space>
            </Space>
          )}
        </section>

        <section className="transport-cargo-block transport-cargo-packaging">
          <Text type="secondary" className="transport-cargo-label">
            {t('transport:packagingEditorTitle')}
          </Text>
          <Text type={hasPackaging ? 'secondary' : 'warning'} className="transport-cargo-packaging-summary">{packagingSummary}</Text>
          {packagingEditor}
        </section>
      </div>

      {canEditTransportCosts ? (
        <section
          id={buildTransportGoodsValueAnchorId(record.key)}
          className={costAllocationMethod === 'MANUAL'
            ? 'transport-cargo-value transport-cargo-value--manual'
            : 'transport-cargo-value'}
          data-testid="transport-cargo-value-bar"
        >
          <div className="transport-cargo-value-main">
            {renderSourcePurchaseValue()}
            <div className="transport-cargo-calculation-value">
              <Text type="secondary" className="transport-cargo-label">
                {t('transport:goodsValueHint.calculationValueLabel')}
              </Text>
              <Form.Item
                name={['cargoItemsByKey', record.key, 'goodsValueTotal']}
                style={{ marginBottom: 0 }}
                rules={[{
                  type: 'number',
                  required: Number(record.quantity || 0) > 0,
                  min: 0,
                  max: MAX_MONEY_AMOUNT,
                  message: t('transport:commercialAllocationValueOutOfRange', { max: MAX_MONEY_AMOUNT }),
                }]}
              >
                <GoodsValueControl
                  currency={displayCurrency}
                  onOperatorEdit={() => onGoodsValueTouched(record.key)}
                />
              </Form.Item>
            </div>
            {hintProvenance?.kind === 'currencyMismatch'
              && purchaseDisplay
              && !hasOriginalPurchaseValue ? (
              <Text className="transport-cargo-source-value">
                {t('transport:goodsValueHint.sourceValue', {
                  value: formatMoneyPl(purchaseDisplay.plnAmount, SUPPLIER_HINT_CURRENCY),
                })}
              </Text>
            ) : null}
            {renderGoodsValueProvenance()}
          </div>
          {costAllocationMethod === 'MANUAL' ? (
            <div className="transport-cargo-manual-cost">
              <Form.Item
                name={['cargoItemsByKey', record.key, 'manualAllocatedTransportCost']}
                label={t('transport:commercialAllocationAllocatedTransport')}
                style={{ marginBottom: 0 }}
                rules={[{
                  type: 'number',
                  min: 0,
                  max: MAX_MONEY_AMOUNT,
                  message: t('transport:commercialAllocationValueOutOfRange', { max: MAX_MONEY_AMOUNT }),
                }]}
              >
                <InputNumber
                  precision={2}
                  addonAfter={displayCurrency}
                  style={{ width: '100%' }}
                  {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                />
              </Form.Item>
            </div>
          ) : null}
        </section>
      ) : null}

      {canEditTransportCosts && showUnitCosts ? (
        <section className="transport-cargo-unit-costs" aria-label={t('transport:unitCostsTitle')}>
          {unitCostItems.map(([label, value]) => (
            <div key={label} className="transport-cargo-unit-cost">
              <Text type="secondary" className="transport-cargo-label">{label}</Text>
              <Text>{formatCurrencyAmount(value)}</Text>
            </div>
          ))}
        </section>
      ) : null}
    </article>
  );
};

export default TransportCargoItemCard;
